'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import Modal from '@/components/Modal';

import { Link } from '@/i18n/routing';
import { useNewsletterModal } from '@/providers/NewsletterModalProvider';

import NewsletterRegistrationView from './NewsletterRegistrationView';

/**
 * Newsletter signup in a modal, opened by the bell icons on Specials, Premieres and the Highlights
 * section (CIN-13995).
 *
 * Restored from the pre-CIN-13768 modal, but wrapping the current Figma-styled
 * NewsletterRegistrationView rather than the removed NewsletterSubscribeUI — the modal is only the
 * container, so there is one signup form to maintain, not two.
 *
 * It passes the entry point's topics straight through, so the modal pre-selects only those. The
 * standalone /newsletter page keeps its all-topics-on default.
 */
export default function NewsletterSubscribeModal() {
    const t = useTranslations();
    const { isOpen, closeModal, preselectedTopics } = useNewsletterModal();

    const handleClose = useCallback(() => {
        closeModal();
    }, [closeModal]);

    return (
        isOpen && (
            <Modal
                modalIsOpen={isOpen}
                setModalIsOpen={(open) => {
                    if (!open) handleClose();
                }}
                className="max-w-[660px]"
                onClose={handleClose}>
                {/* Modal clips its children (`overflow-hidden` on the panel) and the form is taller
                    than the capped panel height, so the scroll container has to live here.
                    `min-h-0` is required — as a flex child it would otherwise refuse to shrink
                    below its content height and the submit button would stay out of reach. */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                    <NewsletterRegistrationView
                        topicPreselection={preselectedTopics}
                        onSubscribed={handleClose}
                    />
                    {/* "Just open the settings page, if someone wants to" — the modal is the quick
                        path, the full topic list stays one click away. */}
                    <div className="flex justify-center pb-16">
                        <Link
                            href="/newsletter"
                            onClick={handleClose}
                            className="text-body-12 underline hover:opacity-80 transition-opacity text-[var(--primary-color)]">
                            {t('newsletter-modal.open-settings')}
                        </Link>
                    </div>
                </div>
            </Modal>
        )
    );
}
